<template>
  <div
    class="overflow-hidden transition-all absolute z-20"
    ref="target"
    :style="styleList"
  >
    <slot />
  </div>
</template>

<script setup>
import { watchEffect, ref, computed } from 'vue'
import { useDynamicPanelWidth, useCommonEventEmitters } from '@/Components/Panels/panel'
import { useScreens } from '@/Composables/screens'

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  location: {
    type: String,
    default: 'left'
  },
  width: {
    type: String,
    default: '300px'
  },
  widths: {
    type: Array,
    default: []
  },
  height: {
    type: String,
    default: '300px'
  }
})

const emits = defineEmits(['clickOutside', 'escPressed'])
const target = ref(null)
useCommonEventEmitters(target, emits)

const { isNarrowScreen } = useScreens()

const { panelWidth } = useDynamicPanelWidth(props.width, props.widths)

const verticalLocations = ['top', 'bottom']
const pullBackLocations = ['top', 'left']

const style = ref('')

const isVerticalPanel = computed(() => verticalLocations.includes(props.location))

const baseStyleList = computed(() => {
  return isVerticalPanel.value
    ? `width:100%; height:${props.height}; ${props.location}:0;`
    : `width:${panelWidth.value}; height:100%; ${props.location}:0;`
})

// Creates a translateY or translateX transform rule based on the panel's location.
// Translation value is one of: `0`, `105%`, or `-105%`. (105 to hide any shadows)
// ex: `transform: translateX(-105%)`
watchEffect(() => {
  const usePullBackStrategy = pullBackLocations.includes(props.location)
  const rule = isVerticalPanel.value ? 'translateY' : 'translateX'
  const position = 105 * (props.open ? 0 : usePullBackStrategy ? -1 : 1)
  style.value = `transform:${rule}(${position}%);`

  if (!isVerticalPanel.value && isNarrowScreen.value) {
    style.value = `${style.value} position: fixed; top: 0; bottom:0; right: 0; z-index: 1000;`
  }
})

const styleList = computed(() => `${baseStyleList.value} ${style.value}`)
</script>
